import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/layout/SiteChrome";
import { useSell, sellStore } from "../store/sellStore";
import { TIME_SLOTS } from "../components/schedule/TimeSlotPicker";
import { formatNice } from "../utils/dateUtils";
import { request } from "../services/api";
import { generateOrderPDF } from "../utils/generateOrderPDF";
import { imageUrl } from "../services/config";

function Row({ icon, label, val }: { icon: string; label: string; val: string }) {
  return (
    <div style={{ display:"flex",alignItems:"flex-start",gap:10,padding:"9px 0",borderBottom:"1px solid #f1f5f9" }}>
      <span style={{ fontSize:16,flexShrink:0,marginTop:1 }}>{icon}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:11,color:"var(--text3)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:2 }}>{label}</div>
        <div style={{ fontSize:14,fontWeight:500,color:"var(--text)" }}>{val || "—"}</div>
      </div>
    </div>
  );
}

export default function ConfirmationPage() {
  const sell = useSell();
  const nav  = useNavigate();
  const [brandName, setBrandName] = React.useState("");

  useEffect(() => {
    if (!sell.referenceNo) { nav("/"); return; }
    if (sell.brandId) {
      request('/nodes').then(data => {
        const nodes = data.nodes || []
        const b = nodes.find((n: any) => n._id === sell.brandId);
        if (b) setBrandName(b.name);
      }).catch(console.error);
    }
  }, [sell.referenceNo, sell.brandId, nav]);

  if (!sell.referenceNo) return null;

  const model   = sell.product;
  console.log('[Confirm] product:', model?.name, 'images:', model?.images);
  const slot    = TIME_SLOTS.find(s => s.value === sell.pickup?.timeSlot);
  const address = [sell.pickup?.address, sell.pickup?.landmark, sell.pickup?.city, sell.pickup?.pincode].filter(Boolean).join(", ");

  const details = [
    { icon:"📱", label:"Device",        val:`${brandName} ${model?.name || ""}`.trim() },
    { icon:"💰", label:"Payout Amount", val:`₹${sell.finalPrice?.toLocaleString("en-IN") || "0"}` },
    { icon:"👤", label:"Name",          val:sell.pickup?.name || "" },
    { icon:"📞", label:"Phone",         val:sell.phone ? `+91 ${sell.phone}` : "" },
    { icon:"📅", label:"Pickup Date",   val:formatNice(sell.pickup?.date || "") },
    { icon:"⏰", label:"Time Slot",     val:slot?.label || sell.pickup?.timeSlot || "" },
    { icon:"📍", label:"Address",       val:address },
    ...(sell.orderId ? [{ icon:"🔗", label:"Order ID", val:sell.orderId }] : []),
  ];

  return (
    <div className="page-bg">
      <Header />
      <div style={{ maxWidth:560,margin:"0 auto",padding:"32px 16px 80px" }}>

        {/* Hero success card */}
        <div style={{ background:"linear-gradient(135deg,#1D9E75,#0F6E56)",borderRadius:22,padding:"36px 28px",textAlign:"center",marginBottom:20,boxShadow:"0 12px 40px rgba(29,158,117,0.3)" }}>
          <div style={{ width:72,height:72,borderRadius:"50%",background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 16px",animation:"bounceIn 0.5s cubic-bezier(0.34,1.56,0.64,1)" }}>🎉</div>
          <h1 style={{ fontFamily:"var(--font-display)",fontWeight:800,fontSize:"clamp(20px,4vw,26px)",color:"#fff",marginBottom:8,letterSpacing:"-0.5px" }}>Booking Confirmed!</h1>
          <p style={{ fontSize:14,color:"rgba(255,255,255,0.8)",marginBottom:20 }}>Your pickup has been scheduled. Our technician will arrive on time.</p>
          <div style={{ background:"rgba(255,255,255,0.15)",borderRadius:12,padding:"14px 20px",display:"inline-block" }}>
            <div style={{ fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.7)",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4 }}>Reference Number</div>
            <div style={{ fontFamily:"var(--font-display)",fontWeight:800,fontSize:22,color:"#fff",letterSpacing:"0.05em" }}>{sell.referenceNo}</div>
          </div>
        </div>

        {/* Payout highlight */}
        <div style={{ background:"var(--surface)",border:"2px solid #1D9E75",borderRadius:18,padding:"20px 22px",marginBottom:16,boxShadow:"0 4px 20px rgba(29,158,117,0.12)" }}>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12 }}>
            <div>
              <div style={{ fontSize:12,color:"var(--text2)",fontWeight:600,marginBottom:4 }}>💰 You will receive</div>
              <div style={{ fontFamily:"var(--font-display)",fontWeight:800,fontSize:"clamp(28px,5vw,38px)",color:"#1D9E75",letterSpacing:"-0.5px" }}>
                ₹{sell.finalPrice?.toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize:12,color:"var(--text3)",marginTop:3 }}>Paid via Cash / NEFT / UPI after inspection</div>
            </div>
            <div style={{ background:"#dcfce7",borderRadius:12,padding:"12px 18px",textAlign:"center" }}>
              <div style={{ fontSize:28,marginBottom:4 }}>🔧</div>
              <div style={{ fontSize:12,fontWeight:700,color:"#16a34a" }}>Free Pickup</div>
              <div style={{ fontSize:11,color:"#15803d" }}>Doorstep Service</div>
            </div>
          </div>
        </div>

        {/* Order details */}
        <div style={{ background:"var(--surface)",border:"1.5px solid var(--border)",borderRadius:18,padding:"20px 22px",marginBottom:16 }}>
          <h2 style={{ fontFamily:"var(--font-display)",fontWeight:700,fontSize:16,color:"var(--text)",marginBottom:14 }}>📋 Booking Details</h2>
          {details.map((d,i) => <Row key={i} {...d} />)}
        </div>

        {/* What happens next */}
        <div style={{ background:"var(--surface)",border:"1.5px solid var(--border)",borderRadius:18,padding:"20px 22px",marginBottom:20 }}>
          <h2 style={{ fontFamily:"var(--font-display)",fontWeight:700,fontSize:16,color:"var(--text)",marginBottom:14 }}>🚀 What Happens Next?</h2>
          {[
            { step:"1", icon:"📲", title:"SMS Confirmation",  desc:"You'll receive an SMS with your booking details" },
            { step:"2", icon:"🔧", title:"Technician Assigned", desc:"A verified technician will be assigned to your order" },
            { step:"3", icon:"🚪", title:"Doorstep Pickup",   desc:"Technician arrives at your address at scheduled time" },
            { step:"4", icon:"🔍", title:"Device Inspection",  desc:"Quick inspection to verify device condition" },
            { step:"5", icon:"💰", title:"Instant Payment",   desc:"Get paid immediately — cash, NEFT, or UPI" },
          ].map(s => (
            <div key={s.step} style={{ display:"flex",gap:12,marginBottom:14,alignItems:"flex-start" }}>
              <div style={{ width:28,height:28,borderRadius:"50%",background:"#1D9E75",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,flexShrink:0 }}>{s.step}</div>
              <div>
                <div style={{ fontWeight:600,fontSize:14,color:"var(--text)",display:"flex",alignItems:"center",gap:6 }}>
                  <span>{s.icon}</span> {s.title}
                </div>
                <div style={{ fontSize:12,color:"var(--text2)",marginTop:2 }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
          <button
            onClick={() => {
              generateOrderPDF({
                referenceNo:    sell.referenceNo || "",
                orderId:        sell.orderId,
                deviceName:     model?.name || "",
                brandName:      brandName,
                payoutAmount:   sell.finalPrice || 0,
                customerName:   sell.pickup?.name || "",
                customerPhone:  sell.phone || "",
                pickupDate:     formatNice(sell.pickup?.date || ""),
                timeSlot:       slot?.label || sell.pickup?.timeSlot || "",
                address:        address,
                productImageUrl: imageUrl(model?.images?.[0]?.url),
              });
            }}
            style={{ width:"100%",borderRadius:12,border:"1.5px solid var(--border)",background:"var(--surface)",padding:"13px",fontFamily:"var(--font-display)",fontSize:14,fontWeight:600,color:"var(--text)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,transition:"all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "var(--bg)"; e.currentTarget.style.borderColor = "#1D9E75"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.borderColor = "var(--border)"; }}>
            📄 Download PDF Receipt
          </button>
          <Link to="/my-orders" style={{ width:"100%",borderRadius:12,border:"1.5px solid var(--border)",background:"var(--surface)",padding:"13px",fontFamily:"var(--font-display)",fontSize:14,fontWeight:600,color:"var(--text)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,transition:"all 0.2s",textDecoration:"none" }}
            onMouseEnter={e => { e.currentTarget.style.background = "var(--bg)"; e.currentTarget.style.borderColor = "var(--indigo)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.borderColor = "var(--border)"; }}>
            📦 View My Orders
          </Link>
          <button
            onClick={() => { sellStore.reset(); nav("/"); }}
            style={{ width:"100%",borderRadius:12,border:"none",background:"linear-gradient(135deg,#1D9E75,#0F6E56)",padding:"14px",fontFamily:"var(--font-display)",fontSize:15,fontWeight:700,color:"#fff",cursor:"pointer",boxShadow:"0 4px 14px rgba(29,158,117,0.3)" }}>
            Sell Another Device →
          </button>
        </div>

        <p style={{ textAlign:"center",fontSize:12,color:"var(--text3)",marginTop:20,lineHeight:1.6 }}>
          Need help? Contact us at <strong>support@truevaluemobiles.in</strong><br />
          Reference: <strong>{sell.referenceNo}</strong>
        </p>
      </div>
    </div>
  );
}
